import React, { useState, useEffect, useCallback } from 'react'
import { Button, useErrorService } from '@abroad/components'
import { Helmet } from 'react-helmet'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useStripe } from '@stripe/react-stripe-js'
import { PageHeader } from '../common'
import { EXPERIENCE_PAYMENT_LOCK_STORAGE_KEY } from './ExperiencePaymentLock'

const QuestPaymentStatus = () => {
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const history = useHistory()
  const stripe = useStripe()
  const Error = useErrorService()
  const { questId } = useParams()
  const location = useLocation()
  const query = new URLSearchParams(location?.search)
  const redirect_status = query.get('redirect_status')
  const locationSearch = location?.search

  useEffect(() => {
    const params = new URLSearchParams(locationSearch)
    const queryClientSecret = params.get('payment_intent_client_secret')
    if (queryClientSecret) {
      setClientSecret(queryClientSecret)
    }
  }, [locationSearch])

  useEffect(() => {
    if (redirect_status && redirect_status === 'failed') {
      setPaymentStatus('requires_payment_method')
    } else if (redirect_status && redirect_status === 'pending') {
      // This is set when user returns from ACH micro-deposit verification
      // and closes the pending modal - we'll check the actual status below
      setPaymentStatus(null)
    }
  }, [redirect_status])

  const checkPaymentStatus = useCallback(async () => {
    if (!stripe || !clientSecret) return null

    try {
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret)
      switch (paymentIntent.status) {
        case 'succeeded':
          setPaymentStatus('succeeded')
          break
        case 'processing':
          setPaymentStatus('processing')
          break
        case 'requires_payment_method':
          setPaymentStatus('requires_payment_method')
          break
        case 'requires_action':
          // ACH payment pending micro-deposit verification
          setPaymentStatus('requires_action')
          break
        default:
          alert('Something went wrong.')
      }
      return paymentIntent.status
    } catch (error) {
      Error.showError(error)
      return null
    }
  }, [stripe, clientSecret, Error])

  useEffect(() => {
    if (
      clientSecret &&
      stripe &&
      (redirect_status === 'succeeded' || redirect_status === 'pending')
    ) {
      let pollCount = 0
      const maxPolls = 15 // 30 seconds total (15 * 2 seconds)
      const pollInterval = 2000 // 2 seconds

      const pollPaymentStatus = async () => {
        const status = await checkPaymentStatus()
        pollCount++

        if (status === 'processing' && pollCount < maxPolls) {
          setTimeout(pollPaymentStatus, pollInterval)
        }
      }

      pollPaymentStatus()
    }
  }, [stripe, clientSecret, redirect_status, checkPaymentStatus])

  useEffect(() => {
    if (!clientSecret || !stripe) return

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkPaymentStatus()
      }
    }

    const handleFocus = () => {
      checkPaymentStatus()
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clientSecret, stripe, checkPaymentStatus])

  const renderPageHelmet = () => {
    return (
      <Helmet>
        <title>{'Quest Payment | Abroad'}</title>
        <meta name='title' content={'Quest Payment | Abroad'}></meta>
        <meta property='og:title' content={'Quest Payment | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Quest Payment | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/quests`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
      </Helmet>
    )
  }

  const renderPageHeader = () => {
    return (
      <PageHeader
        showBackArrow={true}
        version='small'
        title='Your Experiences'
        backArrowAction={() => history.replace('/your-experiences')}
      />
    )
  }

  useEffect(() => {
    if (paymentStatus === 'succeeded' && questId) {
      // Clear experience paywall lock so home doesn't bounce back to payment
      try {
        sessionStorage.removeItem(EXPERIENCE_PAYMENT_LOCK_STORAGE_KEY)
      } catch (e) {
        // ignore
      }
      const timer = setTimeout(() => {
        history.replace(`/your-experiences?questId=${questId}`)
      }, 2500)

      return () => clearTimeout(timer)
    }
  }, [paymentStatus, questId, history])

  const renderPaymentStatus = () => {
    if (!paymentStatus) {
      return null
    }
    if (paymentStatus === 'succeeded') {
      return (
        <div className='text-center mt-4'>
          <h2 className='mb-4 s2'>Thank you!</h2>
          <div className='s6 mb-4'>
            Your payment has been successfully processed.
            <br />
            You'll receive a confirmation email shortly.
            <br />
            Redirecting you to your experience page...
          </div>
          <Button
            type='submit'
            variant='saffron'
            className='text-uppercase line-height-normal s9 border-radius-normal btn-fixed-height'
            size='md'
            onClick={() =>
              history.replace(`/your-experiences?questId=${questId}`)
            }>
            Go To Experience Page
          </Button>
        </div>
      )
    } else if (paymentStatus === 'processing') {
      return (
        <div className='text-center mt-5'>
          <h2 className='mb-4 s2'>Processing Payment</h2>
          <div className='s6 mb-4'>
            This may take may take 2-3 days. We will let you know once payment
            is processed.
          </div>
          <Button
            variant='saffron'
            className='text-uppercase line-height-normal s9 border-radius-normal btn-fixed-height'
            size='md'
            onClick={() => {
              // checkPaymentStatus()
              history.replace(`/your-experiences?questId=${questId}`)
            }}>
            Back to Your Experiences
          </Button>
        </div>
      )
    } else if (paymentStatus === 'requires_action') {
      return (
        <div className='text-center mt-5'>
          <h2 className='mb-4 s2'>Verification Pending</h2>
          <div className='s6 mb-4'>
            Your bank account verification is pending.
            <br />
            Micro-deposits can take 1-2 business days to appear in your account.
            <br />
            Once verified, your payment will be completed automatically.
          </div>
          <Button
            variant='saffron'
            className='text-uppercase line-height-normal s9 border-radius-normal btn-fixed-height'
            size='md'
            onClick={() => {
              history.replace(`/your-experiences?questId=${questId}`)
            }}>
            Back to Your Experiences
          </Button>
        </div>
      )
    } else {
      return (
        <div className='text-center mt-5'>
          <h2 className='mb-4 s2' style={{ color: 'red' }}>
            Payment Failed!
          </h2>
          <div className='s6 mb-4'>
            Your payment was unsuccessful. Please try again.
          </div>
          <Button
            variant='saffron'
            className='border-radius-normal btn-same-width btn-fixed-height'
            size='md'
            onClick={() => {
              history.replace(`/your-experiences/${questId}/payments`)
            }}>
            <span className='s10a px-40px py-13px'>TRY AGAIN</span>
          </Button>
        </div>
      )
    }
  }

  return (
    <>
      {renderPageHelmet()}
      <main>
        {renderPageHeader()}
        <div className='pt-5'>{renderPaymentStatus()}</div>
      </main>
    </>
  )
}

export default QuestPaymentStatus
